import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { searchAppsByDeveloper, AppData } from '../services/appstore';
import { trackClick } from '../utils/analytics';
import {
  MinimalGlobalStyle,
  Container,
  BackButton,
  Header,
  PageTitle,
  Bio,
  HeaderLinks,
  HeaderLink,
  Section,
  SectionTitle,
} from '../components/minimal';

const IOS_ACCENT = '#fb923c';

const WideInner = styled.div`
  max-width: 960px;
  margin: 0 auto;
  padding: 5rem 2rem 6rem;

  @media (max-width: 768px) {
    padding: 4rem 1.25rem 4rem;
  }
`;

const AppGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1.5rem;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const AppCardLink = styled.a`
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
  padding: 1.25rem;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  text-decoration: none;
  color: inherit;
  transition: border-color 0.2s ease, background 0.2s ease;

  &:hover {
    border-color: rgba(251, 146, 60, 0.35);
    background: rgba(255, 255, 255, 0.05);

    h3 {
      color: ${IOS_ACCENT};
    }
  }
`;

const AppHeader = styled.div`
  display: flex;
  gap: 0.875rem;
  align-items: center;
`;

const AppIcon = styled.img`
  width: 64px;
  height: 64px;
  border-radius: 14px;
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.05);
`;

const AppInfo = styled.div`
  min-width: 0;
  flex: 1;
`;

const AppTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 0.15rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition: color 0.15s ease;
`;

const AppGenre = styled.div`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.4);
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const AppDescription = styled.p`
  font-size: 0.8125rem;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.55;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const AppMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.4);
  padding-top: 0.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
`;

const Rating = styled.span`
  font-variant-numeric: tabular-nums;
`;

const Status = styled.div`
  color: rgba(255, 255, 255, 0.4);
  text-align: center;
  padding: 3rem 0;
  font-size: 0.875rem;
`;

const APP_ORDER = [
  'Overboard', 'Rekishi', 'RideBoost', 'Sportbuddy', 'CozyTime', 'LifeCommits',
  'Blip', 'Crunch', 'Arc', 'Scribble', 'Green', 'Linky', 'Prysm', 'Rootine',
  'BirdieBook', 'LocalAI', 'Orb', 'Sky', 'Weeb', 'Aeth', 'Neb', 'Lock',
  'Myth', 'Fact',
];

const sortApps = (apps: AppData[]): AppData[] =>
  [...apps].sort((a, b) => {
    const ai = APP_ORDER.findIndex(n => a.trackName.toLowerCase().includes(n.toLowerCase()));
    const bi = APP_ORDER.findIndex(n => b.trackName.toLowerCase().includes(n.toLowerCase()));
    if (ai !== -1 && bi !== -1) return ai - bi;
    if (ai !== -1) return -1;
    if (bi !== -1) return 1;
    return a.trackName.localeCompare(b.trackName);
  });

const IOSAppsPage: React.FC = () => {
  const [apps, setApps] = useState<AppData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        let data = await searchAppsByDeveloper('Rekishi, LLC');
        if (!data.length) data = await searchAppsByDeveloper('Rekishi');
        setApps(sortApps(data));
      } catch (err) {
        console.error('Error fetching apps:', err);
        setError('Failed to load apps.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleAppClick = (app: AppData) => {
    trackClick('ios_app', {
      app_name: app.trackName,
      app_id: app.trackId,
      app_genre: app.primaryGenreName,
    });
  };

  return (
    <>
      <MinimalGlobalStyle $accent={IOS_ACCENT} />
      <Container>
        <BackButton />
        <WideInner>
          <Header>
            <PageTitle>iOS Apps</PageTitle>
            <Bio>
              Native apps for iPhone, iPad, Apple Watch, and Vision Pro. Shipped through
              Rekishi, LLC.
            </Bio>
            <HeaderLinks>
              <HeaderLink
                href="https://apps.apple.com/us/developer/rekishi-llc/id1615636751"
                target="_blank"
                rel="noopener noreferrer"
              >
                All apps on App Store
              </HeaderLink>
            </HeaderLinks>
          </Header>

          <Section>
            <SectionTitle>{apps.length || '—'} Apps in Production</SectionTitle>
            {loading && <Status>Loading apps…</Status>}
            {error && <Status>{error}</Status>}
            {!loading && !error && apps.length === 0 && <Status>No apps found.</Status>}
            {apps.length > 0 && (
              <AppGrid>
                {apps.map(app => (
                  <AppCardLink
                    key={app.trackId}
                    href={app.trackViewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => handleAppClick(app)}
                  >
                    <AppHeader>
                      <AppIcon src={app.artworkUrl512} alt={app.trackName} loading="lazy" />
                      <AppInfo>
                        <AppTitle>{app.trackName}</AppTitle>
                        <AppGenre>{app.primaryGenreName}</AppGenre>
                      </AppInfo>
                    </AppHeader>
                    <AppDescription>{app.description}</AppDescription>
                    <AppMeta>
                      <Rating>
                        {app.averageUserRating > 0
                          ? `★ ${app.averageUserRating.toFixed(1)} · ${app.userRatingCount} ratings`
                          : 'No ratings yet'}
                      </Rating>
                      <span>v{app.version}</span>
                    </AppMeta>
                  </AppCardLink>
                ))}
              </AppGrid>
            )}
          </Section>
        </WideInner>
      </Container>
    </>
  );
};

export default IOSAppsPage;
